import type { AnalyticsBreakdownItem, AnalyticsSeriesPoint } from '@sia/analytics-contracts';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';

const chartColors = {
  primary: '#5B4B8A',
  lilac: '#E3D9F3',
  median: '#9A86C4',
  grid: '#DCE3DA',
  muted: '#6F7C74',
  text: '#203029',
  background: '#F3F3F3',
};

const mondayFirstWeekdays = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

export function orderBreakdownData(title: string, data: AnalyticsBreakdownItem[]) {
  if (title !== 'Listing arrivals by weekday') return data;
  const weekdayOrder = new Map(mondayFirstWeekdays.map((weekday, index) => [weekday, index]));
  return [...data].sort((left, right) =>
    (weekdayOrder.get(left.label) ?? Number.MAX_SAFE_INTEGER)
      - (weekdayOrder.get(right.label) ?? Number.MAX_SAFE_INTEGER));
}

export function breakdownAxisWidth(data: AnalyticsBreakdownItem[]) {
  const longestLabel = data.reduce((longest, item) => Math.max(longest, item.label.length), 0);
  return Math.min(144, Math.max(64, Math.min(longestLabel, 18) * 7 + 16));
}

function AccessibleTable({ title, rows }: { title: string; rows: { label: string; value: number | string }[] }) {
  return <table className="sr-only">
    <caption>{title}</caption><thead><tr><th>Label</th><th>Value</th></tr></thead>
    <tbody>{rows.map((row) => <tr key={row.label}><td>{row.label}</td><td>{row.value}</td></tr>)}</tbody>
  </table>;
}

function axisLabelLines(value: string, lineLength = 22) {
  const words = value.trim().split(/\s+/);
  const lines: string[] = [];

  for (const word of words) {
    const current = lines.at(-1);
    if (!current || (current.length + word.length + 1 > lineLength && lines.length < 2)) {
      lines.push(word);
    } else {
      lines[lines.length - 1] = `${current} ${word}`;
    }
  }

  if (lines.length > 2) lines.splice(2);
  if (lines[1]?.length > lineLength) lines[1] = `${lines[1].slice(0, lineLength - 1).trimEnd()}…`;
  if (words.join(' ') !== lines.join(' ') && !lines[1]?.endsWith('…')) {
    lines[1] = `${(lines[1] ?? '').slice(0, lineLength - 1).trimEnd()}…`;
  }

  return lines;
}

function BreakdownAxisTick({ x = 0, y = 0, payload }: {
  x?: number;
  y?: number;
  payload?: { value?: string };
}) {
  const lines = axisLabelLines(String(payload?.value ?? ''));
  const tickX = x - 8;
  return <text x={tickX} y={y} textAnchor="end" fill={chartColors.text} fontSize={11}>
    {lines.map((line, index) => <tspan key={`${line}-${index}`} x={tickX} dy={index === 0 ? (lines.length === 1 ? '0.35em' : '-0.35em') : '1.2em'}>{line}</tspan>)}
  </text>;
}

export function ActivityChart({ data }: { data: AnalyticsSeriesPoint[] }) {
  if (data.length === 0) return <p className="chart-empty">Awaiting data</p>;
  return <>
    <div className="chart" role="img" aria-label="Daily and monthly active users over the selected range">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
          <CartesianGrid stroke={chartColors.grid} vertical={false} />
          <XAxis dataKey="date" tick={{ fill: chartColors.muted, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={28} />
          <YAxis tick={{ fill: chartColors.muted, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ border: '1px solid #DCE3DA', borderRadius: 16, boxShadow: '0 8px 24px rgba(32,48,41,.08)' }} />
          <Line name="DAU" type="monotone" dataKey="value" stroke={chartColors.primary} strokeWidth={2.4} dot={false} />
          <Line name="MAU" type="monotone" dataKey="secondaryValue" stroke={chartColors.lilac} strokeWidth={3} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <AccessibleTable title="Active-user chart values" rows={data.map((point) => ({ label: point.date, value: point.value }))} />
  </>;
}

export function AccountTrendChart({ data }: { data: AnalyticsSeriesPoint[] }) {
  if (data.length === 0) return <p className="chart-empty">Awaiting data</p>;
  return <>
    <div className="chart chart--account-trend" role="img" aria-label="Total registered accounts over time">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="account-trend-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={chartColors.lilac} stopOpacity={0.9} />
              <stop offset="100%" stopColor={chartColors.lilac} stopOpacity={0.35} />
            </linearGradient>
          </defs>
          <Tooltip
            formatter={(value) => [Number(value).toLocaleString(), 'Accounts']}
            labelFormatter={(label, payload) => String(payload?.[0]?.payload?.date ?? label)}
            contentStyle={{ border: '1px solid #DCE3DA', borderRadius: 16, boxShadow: '0 8px 24px rgba(32,48,41,.08)' }}
          />
          <XAxis dataKey="date" hide />
          <Area type="monotone" dataKey="value" stroke={chartColors.primary} strokeWidth={3} fill="url(#account-trend-fill)" dot={false} activeDot={{ r: 4, fill: chartColors.primary, strokeWidth: 0 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
    <AccessibleTable title="Total registered accounts chart values" rows={data.map((point) => ({ label: point.date, value: point.value }))} />
  </>;
}

export function FailureTooltip({ active, payload, label }: Pick<TooltipContentProps, 'active' | 'payload' | 'label'>) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as AnalyticsSeriesPoint | undefined;
  if (!point) return null;

  return <div className="failure-tooltip">
    <p className="failure-tooltip__date">{point.date ?? label}</p>
    <p className="failure-tooltip__count">{point.value} {point.value === 1 ? 'failure' : 'failures'}</p>
    {point.failureDetails?.length ? (
      <ul className="failure-tooltip__details">
        {point.failureDetails.map((detail, index) => <li key={`${detail.source}-${index}`}><span>{detail.source}:</span> {detail.reason}{detail.action ? <small>Action: {detail.action}</small> : null}</li>)}
      </ul>
    ) : <p className="failure-tooltip__empty">{point.value === 1 ? 'No reason recorded.' : 'No reasons recorded.'}</p>}
  </div>;
}

export function SparklineChart({ title, data, formatAsPercent = false, showFailureDetails = false }: { title: string; data: AnalyticsSeriesPoint[]; formatAsPercent?: boolean; showFailureDetails?: boolean }) {
  if (data.length === 0) return null;
  const formatValue = (value: number) => formatAsPercent
    ? `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
    : value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return <div className="chart chart--sparkline" role="img" aria-label={`${title} trend`}>
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 6, right: 0, bottom: 0, left: 0 }}>
        <CartesianGrid stroke={chartColors.grid} vertical={false} />
        <Tooltip
          content={showFailureDetails ? FailureTooltip : undefined}
          formatter={(value) => [formatValue(Number(value)), title]}
          labelFormatter={(label, payload) => String(payload?.[0]?.payload?.date ?? label)}
          contentStyle={{ border: '1px solid #DCE3DA', borderRadius: 16, boxShadow: '0 8px 24px rgba(32,48,41,.08)' }}
        />
        <XAxis dataKey="date" hide />
        <YAxis
          tick={{ fill: chartColors.muted, fontSize: 10, textAnchor: 'start' }}
          tickFormatter={(value) => formatValue(Number(value))}
          tickLine={false}
          axisLine={false}
          width={16}
          tickMargin={0}
          tickCount={3}
          domain={[0, 'auto']}
          allowDecimals
        />
        <Area type="monotone" dataKey="value" stroke={chartColors.primary} strokeWidth={2.4} fill={chartColors.lilac} fillOpacity={0.5} dot={false} activeDot={{ r: 3, fill: chartColors.primary, strokeWidth: 0 }} />
      </AreaChart>
    </ResponsiveContainer>
  </div>;
}

export function TimeSeriesChart({ title, data }: { title: string; data: AnalyticsSeriesPoint[] }) {
  if (data.length === 0) return <p className="chart-empty">Awaiting data</p>;
  return <>
    <div className="chart" role="img" aria-label={`${title} over the selected range`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
          <CartesianGrid stroke={chartColors.grid} vertical={false} />
          <XAxis dataKey="date" tick={{ fill: chartColors.muted, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={28} />
          <YAxis tick={{ fill: chartColors.muted, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip contentStyle={{ border: '1px solid #DCE3DA', borderRadius: 16, boxShadow: '0 8px 24px rgba(32,48,41,.08)' }} />
          <Line name={title} type="monotone" dataKey="value" stroke={chartColors.primary} strokeWidth={2.4} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <AccessibleTable title={`${title} chart values`} rows={data.map((point) => ({ label: point.date, value: point.value }))} />
  </>;
}

export function ComparisonChart({ title, data, unit }: { title: string; data: AnalyticsSeriesPoint[]; unit: 'count' | 'percent' | 'days' | 'per-day' }) {
  if (data.length === 0) return <p className="chart-empty">Awaiting data</p>;
  const chartData = unit === 'days'
    ? data.map((point) => ({
      ...point,
      value: Math.round(point.value),
      secondaryValue: point.secondaryValue === null || point.secondaryValue === undefined
        ? point.secondaryValue
        : Math.round(point.secondaryValue),
    }))
    : data;
  const formatValue = (value: number) => `${value.toLocaleString(undefined, { minimumFractionDigits: unit === 'per-day' ? 2 : 0, maximumFractionDigits: unit === 'count' || unit === 'per-day' ? 2 : unit === 'days' ? 0 : 1 })}${unit === 'percent' ? '%' : unit === 'days' ? ' days' : unit === 'per-day' ? ' /day' : ''}`;
  return <>
    <div className="chart chart--comparison" role="img" aria-label={`${title}: average and median over the selected range`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
          <CartesianGrid stroke={chartColors.grid} vertical={false} />
          <XAxis dataKey="date" tick={{ fill: chartColors.muted, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={28} />
          <YAxis tick={{ fill: chartColors.muted, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={unit !== 'days'} tickFormatter={unit === 'days' ? (value) => Math.round(Number(value)).toLocaleString() : undefined} />
          <Tooltip
            formatter={(value, name) => [value === null || value === undefined ? '—' : formatValue(Number(value)), String(name)]}
            labelFormatter={(label, payload) => String(payload?.[0]?.payload?.date ?? label)}
            contentStyle={{ border: '1px solid #DCE3DA', borderRadius: 16, boxShadow: '0 8px 24px rgba(32,48,41,.08)' }}
          />
          <Line name="Median" type="monotone" dataKey="secondaryValue" stroke={chartColors.median} strokeWidth={2.4} dot={false} />
          <Line name="Average" type="monotone" dataKey="value" stroke={chartColors.primary} strokeWidth={2.4} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
    <div className="chart-legend" aria-label="Chart key">
      <span><i className="chart-legend__swatch chart-legend__swatch--average" />Average</span>
      <span><i className="chart-legend__swatch chart-legend__swatch--median" />Median</span>
    </div>
    <table className="sr-only"><caption>{title} average and median chart values</caption><thead><tr><th>Date</th><th>Average</th><th>Median</th></tr></thead><tbody>{chartData.map((point) => <tr key={point.date}><td>{point.date}</td><td>{formatValue(point.value)}</td><td>{point.secondaryValue === null || point.secondaryValue === undefined ? '—' : formatValue(point.secondaryValue)}</td></tr>)}</tbody></table>
  </>;
}

export function BreakdownChart({ title, data, formatAsPercent = false }: { title: string; data: AnalyticsBreakdownItem[]; formatAsPercent?: boolean }) {
  if (data.length === 0) return <p className="chart-empty">Awaiting data</p>;
  const isHourlyArrivalChart = title === 'Listing arrival time';
  const isDurationChart = title.toLowerCase().includes('duration') || title.toLowerCase().includes('runtime');
  const formatMinutes = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} min`;
  const formatPercentage = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  const ordered = orderBreakdownData(title, data);
  const shown = isHourlyArrivalChart ? ordered : ordered.slice(0, 12);
  const rowHeight = isHourlyArrivalChart ? 32 : 48;
  const axisWidth = breakdownAxisWidth(shown);
  return <>
    <div className="chart chart--bar" style={{ height: Math.max(290, shown.length * rowHeight) }} role="img" aria-label={`${title} bar chart`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={shown} layout="vertical" margin={{ top: 0, right: 24, bottom: 0, left: 8 }}>
          <CartesianGrid stroke={chartColors.grid} horizontal={false} />
          <XAxis type="number" tick={{ fill: chartColors.muted, fontSize: 11 }} tickFormatter={formatAsPercent ? (value) => formatPercentage(Number(value)) : isDurationChart ? (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 }) : undefined} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="label" width={axisWidth} interval={0} tick={<BreakdownAxisTick />} tickLine={false} axisLine={false} />
          <Tooltip cursor={{ fill: chartColors.background }} formatter={formatAsPercent ? (value) => [formatPercentage(Number(value)), 'Share'] : isDurationChart ? (value) => [formatMinutes(Number(value)), 'Runtime'] : undefined} contentStyle={{ border: '1px solid #DCE3DA', borderRadius: 16 }} />
          <Bar dataKey="value" fill={chartColors.primary} radius={[0, 5, 5, 0]} maxBarSize={18} />
        </BarChart>
      </ResponsiveContainer>
    </div>
    <AccessibleTable title={title} rows={shown.map((item) => ({ label: item.label, value: formatAsPercent ? formatPercentage(item.value) : item.value }))} />
  </>;
}
